import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { runCopilot, runCopilotLive, suggestions } from '../lib/copilot';
import { useAuth } from '../lib/auth';
import { useClaimsEnabled } from '../lib/features';
import RiskBadge from './RiskBadge';
import StatusPill from './StatusPill';
import { Bot, CornerDownLeft, Search, Sparkles, ArrowRight, Loader2, Wrench, BarChart3 } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import { toast } from '../lib/aiToast';

// Chart height adapts to type: horizontal bars grow with row count.
function chartHeight(chart) {
  if (!chart) return 0;
  if (chart.kind === 'bar') {
    const n = chart.option?.series?.[0]?.data?.length || 4;
    const horizontal = chart.option?.yAxis?.type === 'category';
    return horizontal ? Math.max(180, n * 44 + 40) : 248;
  }
  return 252;
}

// Global ⌘K copilot. Defaults to the deterministic Simulated engine (safe for
// demos); "Live AI" routes to the real Claude backend and falls back to the
// simulation on any error.
export default function Copilot({ open, onClose }) {
  const { session } = useAuth();
  const claimsEnabled = useClaimsEnabled();
  const SUGGESTIONS = suggestions(claimsEnabled);
  const nav = useNavigate();
  const practiceId = session?.practice?.id || 'prac_001';
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [sel, setSel] = useState(0);
  const [mode, setMode] = useState('sim'); // 'sim' | 'live' — Simulated is default
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else { setQ(''); setRes(null); setSel(0); }
  }, [open]);

  // Escape closes the copilot regardless of where focus is (input, a
  // suggestion/result button, or nothing). The input's onKeyDown only fires
  // while it's focused, so we also listen at the window level while open.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Simulated mode: answer as you type. Live mode: wait for Enter.
  useEffect(() => {
    if (mode !== 'sim') { setRes(null); return; }
    if (!q.trim()) { setRes(null); setThinking(false); return; }
    setThinking(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setRes(runCopilot(q, practiceId)); setThinking(false); setSel(0); }, 300);
    return () => clearTimeout(timer.current);
  }, [q, practiceId, mode]);

  if (!open) return null;

  const items = res?.items || [];
  const go = (href) => { if (href) { nav(href); onClose(); } };

  const askLive = async () => {
    if (!q.trim()) return;
    setThinking(true); setRes(null); setSel(0);
    try {
      const out = await runCopilotLive(q);
      setRes(out);
    } catch (e) {
      toast.message('Live AI unavailable — using simulation', { description: 'Start the backend with an ANTHROPIC_API_KEY to enable.' });
      setRes({ ...runCopilot(q, practiceId), fellBack: true });
    } finally {
      setThinking(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Escape') return onClose();
    if (mode === 'live' && e.key === 'Enter') { e.preventDefault(); return askLive(); }
    if (!items.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); go(items[sel]?.href); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl dh-card-flush shadow-2xl shadow-slate-900/20 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Input */}
        <div className="flex items-center gap-3 px-4 border-b border-slate-100">
          <Bot className="h-5 w-5 text-violet-500 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={mode === 'live' ? 'Ask Claude… (press ⏎)' : 'Ask anything or run a command…'}
            className="flex-1 py-4 text-[15px] outline-none placeholder:text-slate-400 bg-transparent"
          />
          {thinking ? <Loader2 className="h-4 w-4 text-slate-400 animate-spin" /> : <span className="kbd">esc</span>}
        </div>

        <div className="max-h-[52vh] overflow-y-auto">
          {/* Empty state — suggestions */}
          {!q.trim() && (
            <div className="p-3">
              <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Try asking</div>
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => { setQ(s); if (mode === 'live') setTimeout(askLive, 0); }} className="w-full text-left px-2 py-2 rounded-lg hover:bg-slate-50 flex items-center gap-2.5 text-sm text-slate-700">
                  <Sparkles className="h-3.5 w-3.5 text-violet-400" />{s}
                </button>
              ))}
            </div>
          )}

          {thinking && mode === 'live' && (
            <div className="px-5 py-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Claude is thinking and calling tools…</div>
          )}

          {/* Grounded answer */}
          {res?.answer && (
            <div className="m-3 ai-surface p-3.5">
              <div className="flex items-center gap-2 mb-1 text-[11px] text-violet-700 font-medium">
                <Bot className="h-3.5 w-3.5" />Copilot
                {res.mode === 'live' && <span className="pill pill-violet">Live · Claude</span>}
                {res.fellBack && <span className="pill pill-amber">fell back to simulation</span>}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{res.answer}</div>
            </div>
          )}

          {/* Generated visualization (ECharts spec from the analytics engine) */}
          {res?.chart && (
            <div className="mx-3 mb-2 dh-card p-3.5">
              <div className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5 text-brand-600" />{res.chart.title}</div>
              <ReactECharts option={res.chart.option} style={{ height: chartHeight(res.chart) }} notMerge lazyUpdate opts={{ renderer: 'svg' }} />
              <div className="text-[10px] text-slate-400 mt-1.5">Generated visualization · ECharts · grounded in your practice data</div>
            </div>
          )}

          {/* Tool-call trace (live) */}
          {res?.toolCalls?.length > 0 && (
            <div className="mx-3 mb-2 dh-inset p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Tools called</div>
              <div className="flex flex-wrap gap-1.5">
                {res.toolCalls.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 text-slate-600 rounded-md px-1.5 py-0.5 mono">
                    <Wrench className="h-3 w-3 text-brand-400" />{t.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Result entities */}
          {items.length > 0 && (
            <div className="p-2">
              {items.map((it, i) => (
                <button key={i} onMouseEnter={() => setSel(i)} onClick={() => go(it.href)} className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-3 ${sel === i ? 'bg-brand-50' : 'hover:bg-slate-50'}`}>
                  <Search className="h-4 w-4 text-slate-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{it.label}</div>
                    {it.sub && <div className="text-xs text-slate-500 truncate">{it.sub}</div>}
                  </div>
                  {it.badge && (['high', 'medium', 'low', 'clear'].includes(it.badge) ? <RiskBadge level={it.badge} /> : <StatusPill status={it.badge} />)}
                  {it.href && sel === i && <CornerDownLeft className="h-3.5 w-3.5 text-brand-400 shrink-0" />}
                </button>
              ))}
            </div>
          )}

          {/* Suggested actions */}
          {res?.actions?.length > 0 && (
            <div className="px-3 pb-3 pt-1 border-t border-slate-100 flex flex-wrap gap-2">
              {res.actions.map((a, i) => (
                <button key={i} onClick={() => go(a.href)} className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded-lg px-2.5 py-1.5 hover:bg-brand-100">
                  {a.label}<ArrowRight className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}

          {res && !res.answer && !items.length && !thinking && (
            <div className="px-5 py-8 text-center text-sm text-slate-400">No matches. Try a patient name or a suggestion.</div>
          )}
        </div>

        <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-3 text-[11px] text-slate-400">
          <div className="seg !p-0.5">
            <button className={mode === 'sim' ? 'active' : ''} onClick={() => { setMode('sim'); setRes(null); }}>Simulated</button>
            <button className={mode === 'live' ? 'active' : ''} onClick={() => { setMode('live'); setRes(null); }}>Live AI</button>
          </div>
          <span><span className="kbd">↑</span> <span className="kbd">↓</span> navigate</span>
          <span><span className="kbd">↵</span> {mode === 'live' ? 'ask' : 'open'}</span>
          <span className="ml-auto flex items-center gap-1"><Bot className="h-3 w-3 text-violet-400" />{mode === 'live' ? 'Claude · grounded tools' : 'Grounded in your practice data'}</span>
        </div>
      </div>
    </div>
  );
}
